import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'
import CryptoJS from 'crypto-js'

import { myToaster } from '@interstellar-component'

import { RegisterService } from './service'
import { useApp } from '../../AppContext'
import { encryptPassword } from '@src/services/Helper'

const RegisterContext = createContext()

function RegisterProvider({ children }) {
  const { getSession } = useApp()
  const [cookie, setCookie] = useCookies(['token-backoffice', 'token-backoffice-temp'])
  const [User, setUser] = useState()
  const [isProfileSliderOpen, setIsProfileSliderOpen] = useState(false)
  const [currentModal, setCurrentModal] = useState({
    status: false,
    current: null,
    error: null,
  })

  const handleCurrentModal = useCallback((modal) => {
    if (modal && modal.current) {
      setCurrentModal({
        status: true,
        current: modal.current,
        parent_id: modal.parent_id,
        error: modal.error,
      })
    } else {
      setCurrentModal((e) => ({ ...e, current: null }))
      setTimeout(() => {
        setCurrentModal({ current: null })
      }, 200)
    }
  }, [])

  const register = useCallback(
    async (body) => {
      const formData = new FormData()
      const encryptedPassword = encryptPassword(body.password)

      const payload = {
        name: body.name,
        email: body.email,
        password: encryptedPassword,
      }
      // formData.append('name', body.name)
      // formData.append('email', body.email)
      // formData.append('password', encryptedPassword)

      return await RegisterService.register(payload)
        .then(myToaster)
        .then((result) => {
          localStorage.setItem('RrwF57&aRMoR5Eq23#Mi', result?.data.user_id) // user_id
          // Resend cooldown starts at the initial send (mirrors Forget-password).
          if (result?.data?.countdown_to_new_otp) {
            localStorage.setItem('countdown_to_new_otp', result.data.countdown_to_new_otp)
          }
          return result
        })
        .catch((e) => {
          if (e.code) {
            setCurrentModal({
              status: true,
              current: 'alert-modal',
              error: e,
            })
          } else {
            myToaster(e)
          }
          throw e
        })
    },
    [setCookie]
  )

  const verifyOtp = useCallback(
    async (otpCode) => {
      const userId = localStorage.getItem('RrwF57&aRMoR5Eq23#Mi')

      const payload = { user_id: userId, code: otpCode }

      const result = await RegisterService.verifyOtp(payload).catch((e) => {
        // OTP wrong/expired: surface the error (mirrors resendOtp). Previously it
        // threw silently and the caller only console.error'd it, so a failed
        // verification showed no feedback at all.
        myToaster(e)
        throw e
      })

      setCookie('token-backoffice-temp', result?.data?.access_token, { path: '/' })
      return result
    },
    [setCookie]
  )

  const resendOtp = useCallback(async () => {
    const userId = localStorage.getItem('RrwF57&aRMoR5Eq23#Mi')

    const payload = { user_id: userId }

    // Caller drives the countdown from the response (success payload carries
    // countdown_to_new_otp; a 429 carries retry_after/countdown_to_new_otp).
    return await RegisterService.resendOtp(payload)
      .then((result) => {
        myToaster(result)
        return result
      })
      .catch((e) => {
        myToaster(e)
        throw e
      })
  }, [])

  const getUser = useCallback(
    async () =>
      await RegisterService.detail()
        .then((res) => {
          setUser({ data: res.data })
        })
        .catch(myToaster),
    []
  )

  const changePassword = useCallback(
    async (body) => {
      const formData = new FormData()
      formData.append('old_password', body.old_password)
      formData.append('new_password', body.new_password)
      return await RegisterService.changePassword(formData)
        .then((e) => {
          myToaster(e)
          setIsProfileSliderOpen(false)
          getSession()
        })
        .catch((e) => {
          myToaster(e)
          throw e
        })
    },
    [getSession]
  )

  const updateProfile = useCallback(
    async (body, shouldChangePassword) => {
      const formData = new FormData()
      formData.append('photo', body.photo)
      if (body.delete_photo) {
        formData.append('delete_photo', body.delete_photo)
      } else {
        formData.append('delete_photo', false)
      }
      formData.append('name', body.name)
      formData.append('username', body.username)
      formData.append('email', body.email)
      formData.append('whatsapp', body.whatsapp)
      return await RegisterService.update(formData)
        .then(myToaster)
        .then(getUser)
        .then(getSession)
        .then(shouldChangePassword ? () => {} : () => setIsProfileSliderOpen(false))
        .catch(myToaster)
    },
    [getSession, getUser]
  )

  const contextValue = useMemo(
    () => ({
      register,
      verifyOtp,
      resendOtp,
      getUser,
      User,
      updateProfile,
      changePassword,
      cookie,
      setCookie,
      isProfileSliderOpen,
      setIsProfileSliderOpen,
      currentModal,
      handleCurrentModal,
    }),
    [
      register,
      verifyOtp,
      resendOtp,
      getUser,
      updateProfile,
      User,
      cookie,
      setCookie,
      changePassword,
      isProfileSliderOpen,
      setIsProfileSliderOpen,
      currentModal,
      handleCurrentModal,
    ]
  )

  return <RegisterContext.Provider value={contextValue}>{children}</RegisterContext.Provider>
}

const useRegister = () => {
  const context = useContext(RegisterContext)
  if (context === undefined) {
    throw new Error('useRegister must be used within a RegisterProvider')
  }
  return context
}

export { RegisterProvider, useRegister }
